import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import ImageUpload from '@/components/admin/ImageUpload';
import { adminApi } from '@/lib/api';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', role: '', content: '', image_url: '', rating: 5, is_active: true, order: 0 });

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try { const res = await adminApi.getTestimonials(); setTestimonials(res.data); } 
    catch (error) { toast.error('Failed to fetch testimonials'); } 
    finally { setLoading(false); }
  };

  const resetForm = () => { setFormData({ name: '', role: '', content: '', image_url: '', rating: 5, is_active: true, order: testimonials.length }); setEditing(null); };

  const handleEdit = (t) => { setEditing(t); setFormData(t); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.content) { toast.error('Please fill required fields'); return; }
    try {
      if (editing) { await adminApi.updateTestimonial(editing.id, formData); toast.success('Updated'); }
      else { await adminApi.createTestimonial(formData); toast.success('Created'); }
      setDialogOpen(false); resetForm(); fetchTestimonials();
    } catch (error) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    try { await adminApi.deleteTestimonial(id); toast.success('Deleted'); fetchTestimonials(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold">Testimonials</h1><p className="text-muted-foreground">Client reviews and feedback</p></div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button><Plus className="w-4 h-4 mr-2" /> Add Testimonial</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Testimonial</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Role</Label><Input value={formData.role} onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))} placeholder="Client" /></div>
              </div>
              <div className="space-y-2"><Label>Content *</Label><Textarea value={formData.content} onChange={(e) => setFormData(p => ({ ...p, content: e.target.value }))} rows={4} /></div>
              <ImageUpload label="Client Photo" value={formData.image_url} onChange={(url) => setFormData(p => ({ ...p, image_url: url }))} />
              <div className="space-y-2"><Label>Rating</Label>
                <div className="flex gap-1">{[1,2,3,4,5].map(r => <button key={r} type="button" onClick={() => setFormData(p => ({ ...p, rating: r }))} className={r <= formData.rating ? 'text-yellow-500' : 'text-gray-300'}><Star className="w-5 h-5 fill-current" /></button>)}</div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} /><Label>Active</Label></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit">Save</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? [1,2,3].map(i => <Card key={i} className="animate-pulse"><CardContent className="p-6 h-40 bg-muted"></CardContent></Card>) :
          testimonials.map((t) => (
            <Card key={t.id} data-testid={`testimonial-${t.id}`}>
              <CardContent className="p-6">
                <div className="flex justify-between mb-3">
                  <div><h3 className="font-bold">{t.name}</h3><p className="text-sm text-muted-foreground">{t.role}</p></div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(t)}><Edit className="w-4 h-4" /></Button>
                    <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(t.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-3 mb-2">"{t.content}"</p>
                <div className="flex">{[1,2,3,4,5].map(r => <Star key={r} className={`w-4 h-4 ${r <= t.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />)}</div>
              </CardContent>
            </Card>
          ))}
      </div>
    </div>
  );
};

export default AdminTestimonials;
